import React, { useEffect, useRef, useState } from 'react';

/**
 * S123 — ONE shared live event countdown. Two approved variants:
 *   variant="card" → client-portal showpiece  (LiveControlCenter + ClientViewPage)
 *   variant="chip" → compact admin workspace chip (EventWorkspacePage)
 * Both read the SAME canonical accessor (eventDate || date) + startTime and count
 * to the identical start moment in Eastern. Do not restyle/relabel — spec-exact.
 */

const TZ = 'America/New_York';

// Wall-clock (y,mo,d,h,mi) in a given IANA zone → UTC epoch ms (DST-correct).
function zonedToUtc(y, mo, d, h, mi) {
  const asUTC = Date.UTC(y, mo - 1, d, h, mi, 0);
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: TZ, hour12: false, year: 'numeric', month: '2-digit',
    day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
  const p = Object.fromEntries(dtf.formatToParts(new Date(asUTC)).map((x) => [x.type, x.value]));
  const shown = Date.UTC(+p.year, +p.month - 1, +p.day, +(p.hour === '24' ? 0 : p.hour), +p.minute, +p.second);
  return asUTC - (shown - asUTC);
}

// Today's date (YYYY-MM-DD) in Eastern.
function easternDayStr() {
  const p = Object.fromEntries(new Intl.DateTimeFormat('en-CA', {
    timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(new Date()).map((x) => [x.type, x.value]));
  return `${p.year}-${p.month}-${p.day}`;
}

// Resolve the target start moment from the event. Returns { ts, dayStr }.
function resolveTarget(event) {
  const raw = event?.eventDate || event?.date;
  if (!raw) return null;
  const s = String(raw);
  const dt = s.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
  if (dt) {
    // Full date-time. If it carries an explicit offset/Z, trust it; else Eastern wall-clock.
    if (/[zZ]$|[+-]\d{2}:?\d{2}$/.test(s)) {
      return { ts: new Date(s).getTime(), dayStr: `${dt[1]}-${dt[2]}-${dt[3]}` };
    }
    return { ts: zonedToUtc(+dt[1], +dt[2], +dt[3], +dt[4], +dt[5]), dayStr: `${dt[1]}-${dt[2]}-${dt[3]}` };
  }
  const d = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!d) return null;
  const st = event?.startTime;
  const tm = st && /^(\d{1,2}):(\d{2})/.exec(String(st));
  const h = tm ? +tm[1] : 0;
  const mi = tm ? +tm[2] : 0;
  return { ts: zonedToUtc(+d[1], +d[2], +d[3], h, mi), dayStr: `${d[1]}-${d[2]}-${d[3]}` };
}

function computeState(target) {
  if (!target) return { phase: 'none' };
  const diff = target.ts - Date.now();
  if (diff > 0) {
    const sec = Math.floor(diff / 1000);
    return {
      phase: 'live',
      days: Math.floor(sec / 86400),
      hrs: Math.floor((sec % 86400) / 3600),
      min: Math.floor((sec % 3600) / 60),
      sec: sec % 60,
    };
  }
  return { phase: target.dayStr === easternDayStr() ? 'today' : 'done' };
}

const FIGTREE = "'Figtree', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
const CORMORANT = "'Cormorant Garamond', Georgia, serif";

const pad = (n) => String(n).padStart(2, '0');

// S127 — preset celebratory message from the event type (first match wins).
function presetMessage(event) {
  const t = String(event?.eventType || event?.event_type || event?.type || '').toLowerCase();
  const map = [
    ['mitzvah', 'Mazel Tov!'],
    ['wedding', 'Congratulations!'],
    ['engagement', 'Congratulations!'],
    ['sweet 16', 'Happy Sweet 16!'],
    ['birthday', 'Happy Birthday!'],
    ['anniversary', 'Happy Anniversary!'],
    ['graduation', 'Congratulations, Grad!'],
    ['quince', '\u00A1Felicidades!'],
    ['corporate', "Here's to a great event!"],
  ];
  for (const [k, v] of map) if (t.includes(k)) return v;
  return "Let's celebrate!";
}

// One-time injected keyframes (entrance rise + dot pulse), reduced-motion aware.
const STYLE_ID = 's123-countdown-style';
function ensureStyle() {
  if (typeof document === 'undefined' || document.getElementById(STYLE_ID)) return;
  const el = document.createElement('style');
  el.id = STYLE_ID;
  el.textContent = `
@keyframes s123Rise { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
@keyframes s123Pulse { 0%,100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.35; transform: scale(0.72); } }
.s123-rise { animation: s123Rise 720ms cubic-bezier(0.16,1,0.3,1) both; }
.s123-dot { animation: s123Pulse 2.4s cubic-bezier(0.16,1,0.3,1) infinite; }
@keyframes s127In { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
@keyframes s127Fade { from { opacity: 0; } to { opacity: 1; } }
.s127-swap { animation: s127In 700ms cubic-bezier(0.16,1,0.3,1) both; }
/* Two-state hero stage: ONE fixed box, both states absolutely centered in it,
   crossfading. Without the fixed height the states stacked and the countdown
   caption stayed on screen underneath the celebration. */
.s123-hero { position: relative; height: 214px; border-radius: 22px; overflow: hidden; }
.s123-state { position: absolute; inset: 0; display: flex; flex-direction: column;
  align-items: center; justify-content: center; text-align: center; padding: 18px;
  transition: opacity .9s cubic-bezier(.22,1,.36,1), transform .9s cubic-bezier(.22,1,.36,1); }
.s123-state.out { opacity: 0; transform: scale(.97); pointer-events: none; }
.s123-state.in { opacity: 1; transform: scale(1); }
.s123-cap { display: inline-flex; align-items: center; gap: 9px; margin-top: 22px; }
@media (prefers-reduced-motion: reduce) { .s123-rise { animation: none; } .s123-dot { animation: none; } .s127-swap { animation-name: s127Fade; } }
`;
  document.head.appendChild(el);
}

export default function EventCountdown({ event, variant = 'card', className = '', bare = false }) {
  ensureStyle();
  const targetRef = useRef(resolveTarget(event));
  const [st, setSt] = useState(() => computeState(targetRef.current));
  // S127 — billboard rotation (CLIENT CARD only). 'count' | 'msg'.
  const [mode, setMode] = useState('count');
  const cardRef = useRef(null);

  useEffect(() => {
    targetRef.current = resolveTarget(event);
    setSt(computeState(targetRef.current));
    const id = setInterval(() => setSt(computeState(targetRef.current)), 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event?.eventDate, event?.date, event?.startTime]);

  // S127 — rotate countdown ↔ message for ~2 min from mount, then counter-only.
  // Single timer, cleared on unmount. Pauses while off-screen / tab hidden; the
  // 2-minute clock is real elapsed time from mount. Card variant only.
  useEffect(() => {
    if (variant === 'chip') return;
    const WINDOW_MS = 120000;
    const COUNT_MS = 7000;
    const MSG_MS = 3500;
    const mountAt = Date.now();
    let onScreen = true;
    let timer = null;
    const clear = () => { if (timer) { clearTimeout(timer); timer = null; } };
    const step = (next) => {
      clear();
      if (Date.now() - mountAt >= WINDOW_MS) { setMode('count'); return; } // window over → settle on counter
      if (!onScreen || (typeof document !== 'undefined' && document.visibilityState !== 'visible')) {
        timer = setTimeout(() => step(next), 500); // paused: hold, retry (real clock keeps running)
        return;
      }
      setMode(next);
      timer = setTimeout(() => step(next === 'count' ? 'msg' : 'count'), next === 'count' ? COUNT_MS : MSG_MS);
    };
    setMode('count');
    timer = setTimeout(() => step('msg'), COUNT_MS);
    const onVis = () => {};
    if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVis);
    let io = null;
    if (cardRef.current && typeof IntersectionObserver !== 'undefined') {
      io = new IntersectionObserver((entries) => { onScreen = entries[0]?.isIntersecting ?? true; });
      io.observe(cardRef.current);
    }
    return () => { clear(); if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVis); if (io) io.disconnect(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant]);

  if (st.phase === 'none') return null;

  const isChip = variant === 'chip';
  const numSize = isChip ? 24 : 40;
  const sepSize = isChip ? 20 : 32;
  const lblSize = isChip ? 8.5 : 9;

  const units = [
    { n: st.days, l: 'DAYS' },
    { n: st.hrs, l: 'HRS' },
    { n: st.min, l: 'MIN' },
    { n: st.sec, l: 'SEC' },
  ];

  const numerals = (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: isChip ? 8 : 14, flexWrap: 'nowrap' }} data-testid="countdown-numerals">
      {units.map((u, i) => (
        <React.Fragment key={u.l}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: isChip ? 26 : 58 }}>
            <span style={{
              fontFamily: FIGTREE, fontWeight: 700, fontSize: numSize, lineHeight: 1,
              color: '#FFFFFF', fontVariantNumeric: 'tabular-nums', fontFeatureSettings: '"tnum"',
            }}>{pad(st.phase === 'live' ? u.n : 0)}</span>
            <span style={{
              fontFamily: FIGTREE, fontSize: lblSize, letterSpacing: '1.5px',
              textTransform: 'uppercase', color: '#8A93A6', marginTop: isChip ? 4 : 8,
            }}>{u.l}</span>
          </div>
          {i < units.length - 1 && (
            <span style={{
              fontFamily: FIGTREE, fontWeight: 700, fontSize: sepSize, lineHeight: 1,
              color: '#2A3348', marginTop: isChip ? -1 : -2,
            }}>:</span>
          )}
        </React.Fragment>
      ))}
    </div>
  );

  const endline = (size) => (
    <div style={{ fontFamily: CORMORANT, fontSize: 28, fontWeight: 600, color: st.phase === 'today' ? '#e6c074' : '#FFFFFF', lineHeight: 1.1 }} data-testid="countdown-endline">
      {st.phase === 'today' ? "It's today! 🎉" : 'Completed'}
    </div>
  );

  // ── COMPACT WORKSPACE CHIP ──────────────────────────────────────────────
  if (isChip) {
    // `bare` (S-workspace header): floating gold numerals, no box/frame.
    if (bare) {
      return (
        <div className={`s123-rise ${className}`} data-testid="event-countdown-chip" style={{ display: 'inline-block' }}>
          {st.phase === 'live' ? numerals : endline()}
        </div>
      );
    }
    return (
      <div
        className={`s123-rise ${className}`}
        data-testid="event-countdown-chip"
        style={{
          background: 'rgba(212,175,55,0.06)', border: '1px solid rgba(212,175,55,0.28)',
          borderRadius: 12, padding: '12px 16px', display: 'inline-block',
        }}
      >
        <div style={{ fontFamily: FIGTREE, fontSize: 9.5, letterSpacing: '1.5px', textTransform: 'uppercase', color: '#D4AF37', fontWeight: 600, marginBottom: 8 }}>COUNTDOWN</div>
        {st.phase === 'live' ? numerals : endline()}
      </div>
    );
  }

  // ── CLIENT PORTAL CARD (S126 panel match + S127 message billboard) ──────
  // ONE stage, two states. The countdown state owns the gold-dot caption, so
  // the caption disappears with it when the celebration crossfades in.
  const showMsg = mode === 'msg' && st.phase === 'live';
  const countdownState = (
    <div className={`s123-state ${showMsg ? 'out' : 'in'}`} data-testid="countdown-numerals-state">
      {st.phase === 'live' ? numerals : endline()}
      {st.phase === 'live' && (
        <div className="s123-cap">
          <span className="s123-dot" style={{ width: 8, height: 8, borderRadius: '50%', background: '#D4AF37', display: 'inline-block' }} />
          <span style={{ fontFamily: FIGTREE, color: '#8A93A6', fontSize: 13 }}>Counting down to your celebration</span>
        </div>
      )}
    </div>
  );
  const celebrationState = (
    <div className={`s123-state ${showMsg ? 'in' : 'out'}`} data-testid="countdown-message">
      <div className="cheers">
        <svg className="glass gl" viewBox="0 0 24 46"><path className="wine" d="M9.3 14 L14.7 14 L13.8 20 Q12 22 10.2 20 Z" /><path d="M7 5 L17 5 L14.5 20 Q12 22.5 9.5 20 Z" /><path d="M12 21 V39" /><path d="M8 40 H16" /><circle cx="10.6" cy="10.5" r=".6" /><circle cx="12.4" cy="8.2" r=".5" /></svg>
        <svg className="glass gr" viewBox="0 0 24 46"><path className="wine" d="M9.3 14 L14.7 14 L13.8 20 Q12 22 10.2 20 Z" /><path d="M7 5 L17 5 L14.5 20 Q12 22.5 9.5 20 Z" /><path d="M12 21 V39" /><path d="M8 40 H16" /><circle cx="10.6" cy="10.5" r=".6" /><circle cx="12.4" cy="8.2" r=".5" /></svg>
        <span className="clink">✦</span>
      </div>
      <div style={{ fontFamily: CORMORANT, fontWeight: 700, fontSize: 40, color: '#e6c074', lineHeight: 1.1 }}>{presetMessage(event)}</div>
      <div style={{ fontFamily: FIGTREE, color: '#8A93A6', fontSize: 12.5, marginTop: 6 }}>We can&apos;t wait to celebrate with you</div>
    </div>
  );

  return (
    <div
      ref={cardRef}
      className={`s123-rise s123-hero ${className}`}
      data-testid="event-countdown-card"
      style={{
        background: 'linear-gradient(180deg, rgba(255,255,255,0.035), rgba(255,255,255,0.015))',
        border: '1px solid rgba(212,175,55,0.14)',
        boxShadow: '0 4px 24px rgba(0,0,0,0.3), 0 0 0 1px rgba(212,175,55,0.03)',
      }}
    >
      {countdownState}
      {st.phase === 'live' && celebrationState}
    </div>
  );
}
